import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Factory, Package, BookOpen, MapPin, Award, Bookmark } from 'lucide-react';
import { SUPPLIERS, PRODUCTS, CERTIFICATIONS, COUNTRIES, BRAND_ASSOC_STATUSES } from '../data/mockData';
import { GUIDES, CATEGORY_LANDINGS, COUNTRY_LANDINGS } from '../data/contentData';
import { track, EVENTS } from '../lib/analytics';

// Universal Global Search — modal/dropdown surfacing grouped mock results
// across products, factories, guides, countries, certifications, brand assoc.
const GlobalSearch = ({ open, onClose }) => {
  const [q, setQ] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQ('');
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Debounced global search analytics — query length only, not the term itself
  // (PII-safe; an email or phone typed into search would never be transmitted).
  useEffect(() => {
    if (!q.trim()) return;
    const id = window.setTimeout(() => {
      track(EVENTS.GLOBAL_SEARCH, { query_length: q.length });
    }, 750);
    return () => window.clearTimeout(id);
  }, [q]);

  const results = useMemo(() => {
    if (!q.trim()) return null;
    const term = q.toLowerCase();
    const match = (s) => s.toLowerCase().includes(term);

    const factories = SUPPLIERS.filter(s =>
      match(s.name) || match(s.tagline) || match(s.country) || s.specialties.some(match)
    ).slice(0, 5);

    const products = PRODUCTS.filter(p =>
      match(p.name) || p.tags.some(match) || match(p.category)
    ).slice(0, 5);

    const guides = GUIDES.filter(g => match(g.title) || match(g.summary) || match(g.categoryLabel)).slice(0, 4);

    const countries = Object.values(COUNTRY_LANDINGS).filter(c => match(c.name)).slice(0, 4);

    const categories = Object.values(CATEGORY_LANDINGS).filter(c => match(c.h1) || match(c.intro)).slice(0, 4);

    const certs = CERTIFICATIONS.filter(match).slice(0, 5);

    const brandAssoc = SUPPLIERS.flatMap(s =>
      s.knownBrandAssociations.filter(a => match(a.brand)).map(a => ({ ...a, supplier: s }))
    ).slice(0, 4);

    return { factories, products, guides, countries, categories, certs, brandAssoc };
  }, [q]);

  const go = (path) => { onClose(); navigate(path); };

  if (!open) return null;

  return (
    <div data-testid="global-search" className="fixed inset-0 z-[80]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative mx-auto mt-[5vh] flex max-h-[90vh] w-[95%] max-w-3xl flex-col border border-black bg-white">
        <div className="flex items-center gap-3 border-b border-black px-4 py-3">
          <Search size={16} strokeWidth={1.5} />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products, factories, guides, countries, certifications…"
            className="flex-1 border-none bg-transparent text-sm placeholder:text-neutral-400 focus:outline-none"
          />
          <span className="hidden font-mono-data text-[10px] text-neutral-500 sm:inline-block">ESC</span>
          <button onClick={onClose} className="flex h-7 w-7 items-center justify-center border border-black hover:bg-black hover:text-white" data-testid="global-search-close">
            <X size={12} strokeWidth={1.6} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!results && (
            <div className="px-4 py-6">
              <div className="label-overline text-neutral-500">Try searching for</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {['hoodie', 'denim', 'Portugal', 'GOTS', 'cap factory', 'activewear', 'SMETA', 'Vietnam'].map(t => (
                  <button
                    key={t}
                    onClick={() => setQ(t)}
                    data-testid={`global-search-chip-${t.toLowerCase().replace(/\s+/g, '-')}`}
                    className="label-overline giwahs-tab-ink px-4 py-1.5"
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <Link to="/factories" onClick={onClose} className="border border-black p-4 hover:bg-neutral-50">
                  <Factory size={14} strokeWidth={1.5} />
                  <div className="mt-3 font-display text-sm" style={{ fontWeight: 600 }}>Browse Factories</div>
                </Link>
                <Link to="/products" onClick={onClose} className="border border-black p-4 hover:bg-neutral-50">
                  <Package size={14} strokeWidth={1.5} />
                  <div className="mt-3 font-display text-sm" style={{ fontWeight: 600 }}>Browse Products</div>
                </Link>
                <Link to="/sourcing-guides" onClick={onClose} className="border border-black p-4 hover:bg-neutral-50">
                  <BookOpen size={14} strokeWidth={1.5} />
                  <div className="mt-3 font-display text-sm" style={{ fontWeight: 600 }}>Sourcing Guides</div>
                </Link>
                <Link to="/ai-search" onClick={onClose} className="border border-black p-4 hover:bg-neutral-50">
                  <Search size={14} strokeWidth={1.5} />
                  <div className="mt-3 font-display text-sm" style={{ fontWeight: 600 }}>AI Image Search</div>
                </Link>
              </div>
            </div>
          )}

          {results && (
            <div data-testid="global-search-results">
              {/* Empty */}
              {!results.factories.length && !results.products.length && !results.guides.length && !results.countries.length && !results.categories.length && !results.certs.length && !results.brandAssoc.length && (
                <div className="px-4 py-12 text-center">
                  <div className="label-overline">No results</div>
                  <p className="mt-3 text-neutral-600">Nothing found for "{q}". Try a broader term.</p>
                </div>
              )}

              {results.factories.length > 0 && (
                <Group label="Factories" icon={Factory}>
                  {results.factories.map(s => (
                    <Row key={s.id} onClick={() => go(`/supplier/${s.slug}`)} testId={`gs-factory-${s.id}`}>
                      <div>
                        <div className="font-display text-sm" style={{ fontWeight: 600 }}>{s.name}</div>
                        <div className="font-mono-data text-[11px] text-neutral-500">{s.city}, {s.country} · {s.type} · Trust {s.trustScore}/100</div>
                      </div>
                    </Row>
                  ))}
                </Group>
              )}
              {results.products.length > 0 && (
                <Group label="Products" icon={Package}>
                  {results.products.map(p => (
                    <Row key={p.id} onClick={() => go(`/product/${p.slug}`)} testId={`gs-product-${p.id}`}>
                      <div>
                        <div className="font-display text-sm" style={{ fontWeight: 600 }}>{p.name}</div>
                        <div className="font-mono-data text-[11px] text-neutral-500">MOQ {p.moq} · {p.pricing} · {p.category.replace('-', ' ')}</div>
                      </div>
                    </Row>
                  ))}
                </Group>
              )}
              {results.guides.length > 0 && (
                <Group label="Sourcing Guides" icon={BookOpen}>
                  {results.guides.map(g => (
                    <Row key={g.slug} onClick={() => go(`/sourcing-guides/${g.slug}`)} testId={`gs-guide-${g.slug}`}>
                      <div>
                        <div className="font-display text-sm" style={{ fontWeight: 600 }}>{g.title}</div>
                        <div className="font-mono-data text-[11px] text-neutral-500">{g.categoryLabel} · {g.readingMin} min read</div>
                      </div>
                    </Row>
                  ))}
                </Group>
              )}
              {results.countries.length > 0 && (
                <Group label="Country Sourcing" icon={MapPin}>
                  {results.countries.map(c => (
                    <Row key={c.slug} onClick={() => go(`/manufacturers/${c.slug}`)} testId={`gs-country-${c.slug}`}>
                      <div>
                        <div className="font-display text-sm" style={{ fontWeight: 600 }}>{c.h1}</div>
                        <div className="font-mono-data text-[11px] text-neutral-500">{c.moqTrend}</div>
                      </div>
                    </Row>
                  ))}
                </Group>
              )}
              {results.categories.length > 0 && (
                <Group label="Category Pages" icon={Bookmark}>
                  {results.categories.map(c => (
                    <Row key={c.slug} onClick={() => go(`/category/${c.slug}`)} testId={`gs-category-${c.slug}`}>
                      <div>
                        <div className="font-display text-sm" style={{ fontWeight: 600 }}>{c.h1}</div>
                        <div className="font-mono-data text-[11px] text-neutral-500">{c.intro.slice(0, 80)}…</div>
                      </div>
                    </Row>
                  ))}
                </Group>
              )}
              {results.certs.length > 0 && (
                <Group label="Certifications" icon={Award}>
                  {results.certs.map(c => (
                    <Row key={c} onClick={() => go(`/factories`)} testId={`gs-cert-${c.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className="font-display text-sm" style={{ fontWeight: 600 }}>{c}</div>
                    </Row>
                  ))}
                </Group>
              )}
              {results.brandAssoc.length > 0 && (
                <Group label="Brand Associations" icon={Award}>
                  {results.brandAssoc.map((a, i) => (
                    <Row key={i} onClick={() => go(`/supplier/${a.supplier.slug}`)} testId={`gs-brand-${i}`}>
                      <div>
                        <div className="font-display text-sm" style={{ fontWeight: 600 }}>{a.brand} <span className="label-overline ml-2 text-neutral-500">{a.status}</span></div>
                        <div className="font-mono-data text-[11px] text-neutral-500">via {a.supplier.name} · {a.period}</div>
                      </div>
                    </Row>
                  ))}
                </Group>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-black bg-[#F5F5F0] px-4 py-2.5 flex items-center justify-between font-mono-data text-[10px] text-neutral-500">
          <span>GIWAHS UNIVERSAL SEARCH · MOCK RESULTS</span>
          <span className="hidden sm:inline">↵ TO OPEN</span>
        </div>
      </div>
    </div>
  );
};

const Group = ({ label, icon: Icon, children }) => (
  <div className="border-t border-neutral-200 first:border-t-0">
    <div className="flex items-center gap-2 bg-[#F5F5F0] px-4 py-2 label-overline text-neutral-700">
      <Icon size={12} strokeWidth={1.5} /> {label}
    </div>
    <div>{children}</div>
  </div>
);

const Row = ({ children, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId} className="flex w-full items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3 text-left hover:bg-neutral-50 last:border-b-0">
    <div className="min-w-0 flex-1">{children}</div>
    <ArrowRight size={14} strokeWidth={1.5} className="shrink-0 text-neutral-400" />
  </button>
);

export default GlobalSearch;
